import { api } from "../../SPA/Services/api.service";
import { panelApiUrl } from "../Utils/Constant";

/**
 * Shared request helper for the Panel Contract API.
 *
 * Why not reuse the SPA `get`/`post`/... wrappers from Services/api.service?
 * Those are bound to `apiBaseUrl` (apiURL/basePath) — a different host from
 * `panelApiUrl`. The panel screens therefore call the raw axios instance with
 * an absolute URL, which bypasses the SPA wrappers' `updateHeader()`. That
 * left panel requests relying on whatever Authorization header some earlier
 * SPA call happened to leave on `api.defaults`. Here the header is attached
 * per request instead, so a page that only ever talks to the panel API is
 * still authenticated.
 *
 * Every panel response is shaped:
 *   { status: "OK", response: { action, data }, message }
 * so `unwrap` returns `response.data` and rejects with `message` otherwise —
 * matching the convention the rest of the app already expects.
 */

export interface PanelEnvelope<T = any> {
  status: string;
  response?: { action?: string; data?: T };
  message?: string;
}

/** Paged list payload returned by the `get-*-list` endpoints. */
export interface PagedResult<T> {
  records: T[];
  totalCount: number;
}

export interface ListParams {
  page?: number;
  size?: number;
  searchText?: string;
  order?: string;
  orderBy?: string;
}

const authHeaders = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const authProvider = JSON.parse(localStorage.getItem("authProvider") || '"Internal"');
  return {
    ...(user?.token && { Authorization: `Bearer ${user.token}` }),
    "X-Auth-Provider": authProvider || "Internal",
  };
};

const SUCCESS = ["OK", "CREATED"];

const unwrap = <T,>(res: any): T => {
  const body: PanelEnvelope<T> = res?.data ?? {};
  const status = String(body.status ?? "");
  // Backend returns either "OK" or an HTTP-ish string like "304 NOT_MODIFIED".
  const ok = SUCCESS.some((s) => status === s || status.includes(s));
  if (!ok) {
    throw new Error(body.message || "Request failed");
  }
  return (body.response?.data ?? ({} as T)) as T;
};

/**
 * Dropdown size for the plain collection routes.
 *
 * `GET /region`, `/category`, `/subRegion`, `/vendor`, `/sorItem` and
 * `/template` are paged and answer with `{ records, totalCount }`, so calling
 * them bare returns only the server's default first page. The pickers need the
 * whole list, which is why Manage Catalog asks for `page=0&size=1000` too.
 */
export const DROPDOWN_PAGE = { page: 0, size: 1000 };

/**
 * Coerce a list response to an array.
 *
 * Two shapes reach the callers as "a list": a bare array from the plain
 * collection routes (`GET /region`), and a paged `{ records, totalCount }`
 * from the `get-*-list` routes. On top of that `unwrap` yields `{}` when a
 * response carries no `response.data` at all — so the usual `rows ?? []` is
 * not a guard, since `{} ?? []` is still `{}` and `.map` then throws.
 */
export const toArray = <T = any,>(value: any): T[] => {
  if (Array.isArray(value)) return value as T[];
  if (Array.isArray(value?.records)) return value.records as T[];
  return [];
};

/** Drop undefined/empty params so we don't send `?searchText=undefined`. */
export const toQuery = (params: Record<string, any> = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.append(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

const url = (path: string) => `${panelApiUrl}${path}`;

/** The full URL panelGet requests for these params — DataTable's Export re-requests it page by page. */
export const panelUrl = (path: string, params?: Record<string, any>) => url(path) + toQuery(params);

/**
 * Re-throw with the message the API actually sent.
 *
 * These helpers call the raw axios instance, so a 4xx rejects with axios's own
 * error and `err.message` reads "Request failed with status code 400". That is
 * what the screens were showing even when the body explained exactly what was
 * wrong — creating a duplicate subsidiary answers
 * "Subsidiary Already Exists! Please create new", and none of it reached the
 * user. `unwrap` already throws the body's message for a non-OK 200, so this
 * covers the other half.
 */
const rethrow = (error: any): never => {
  const fromBody =
    error?.response?.data?.message ??
    error?.response?.data?.response?.message ??
    (typeof error?.response?.data?.response?.data === "string" ? error.response.data.response.data : undefined) ??
    error?.data?.message ??
    (typeof error?.response?.data === "string" ? error.response.data : undefined);
  throw new Error(String(fromBody || error?.message || "Request failed"));
};

export const panelGet = <T = any,>(path: string, params?: Record<string, any>): Promise<T> =>
  api.get(url(path) + toQuery(params), { headers: authHeaders() }).then(unwrap<T>)
    .catch(rethrow);

export const panelPost = <T = any,>(path: string, payload?: any, params?: Record<string, any>): Promise<T> =>
  api.post(url(path) + toQuery(params), payload, { headers: authHeaders() }).then(unwrap<T>)
    .catch(rethrow);

export const panelPut = <T = any,>(path: string, payload?: any, params?: Record<string, any>): Promise<T> =>
  api.put(url(path) + toQuery(params), payload, { headers: authHeaders() }).then(unwrap<T>)
    .catch(rethrow);

export const panelDelete = <T = any,>(path: string, params?: Record<string, any>): Promise<T> =>
  api.delete(url(path) + toQuery(params), { headers: authHeaders() }).then(unwrap<T>)
    .catch(rethrow);

/**
 * multipart/form-data POST.
 *
 * The Content-Type (with its boundary) is deliberately NOT set — the browser
 * must generate it from the FormData, and setting it by hand produces a
 * request the server cannot parse.
 */
export const panelPostForm = <T = any,>(path: string, form: FormData): Promise<T> =>
  api.post(url(path), form, { headers: authHeaders() }).then(unwrap<T>)
    .catch(rethrow);

/** multipart/form-data PUT — same Content-Type caveat as panelPostForm. */
export const panelPutForm = <T = any,>(path: string, form: FormData): Promise<T> =>
  api.put(url(path), form, { headers: authHeaders() }).then(unwrap<T>)
    .catch(rethrow);
